import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { FUNCTION_ENDPOINTS, getDefaultHeaders, FunctionResponse, FunctionError } from './db.util';

// Create axios instance
const api: AxiosInstance = axios.create({
  timeout: 30000, // 30 seconds
  headers: getDefaultHeaders(),
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add default headers to all requests
    const defaultHeaders = getDefaultHeaders();
    Object.entries(defaultHeaders).forEach(([key, value]) => {
      config.headers.set(key, value);
    });
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ Response Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data,
    });
    
    // Transform error to our standard format
    const functionError: FunctionError = {
      error: error.response?.data?.error || 'Network Error',
      message: error.response?.data?.message || error.message,
      details: error.response?.data,
    };
    
    return Promise.reject(functionError);
  }
);

// Generic function call helper
export const callFunction = async <T = unknown>(
  endpoint: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<FunctionResponse<T>> => {
  try {
    const response = await api.post(endpoint, data, config);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    const functionError = error as FunctionError;
    return {
      success: false,
      error: functionError.error,
      message: functionError.message,
    };
  }
};

// Specific function call helpers
export const callFcmNotify = async (data: {
  token: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  priority?: 'high' | 'normal' | 'low';
  requireInteraction?: boolean;
  silent?: boolean;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.FCM_NOTIFY, data);
};

export const callSendNotification = async (data: {
  userId: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.SEND_NOTIFICATION, data);
};

export const callSendOrderNotification = async (data: {
  orderId: string;
  userId: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.SEND_ORDER_NOTIFICATION, data);
};

export const callCreatePublicOrder = async (data: {
  storeId: string;
  tableId: string;
  items: Array<{
    menuItemId: string;
    quantity: number;
    specialInstructions?: string;
  }>;
  totalAmount: number;
  paymentMethod: string;
  tipAmount?: number;
  tipPercentage?: number;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.CREATE_PUBLIC_ORDER, data);
};

export const callCreatePublicReservation = async (data: {
  storeId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  reservationDate: string;
  reservationTime: string;
  partySize: number;
  specialRequests?: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.CREATE_PUBLIC_RESERVATION, data);
};

export const callSendStaffInvitation = async (data: {
  email: string;
  storeId: string;
  role: string;
  invitedBy: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.SEND_STAFF_INVITATION, data);
};

export const callSendDemoRequest = async (data: {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.SEND_DEMO_REQUEST, data);
};

export const callSendStorefrontContact = async (data: {
  name: string;
  email: string;
  message: string;
  storeId: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.SEND_STOREFRONT_CONTACT, data);
};

export const callGetUnreadMessages = async (data: {
  userId: string;
  storeId: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.GET_UNREAD_MESSAGES, data);
};

export const callUpdateReadStatus = async (data: {
  messageId: string;
  userId: string;
  isRead: boolean;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.UPDATE_READ_STATUS, data);
};

export const callCreateActivityLog = async (data: {
  userId: string;
  action: string;
  details: Record<string, unknown>;
  storeId?: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.CREATE_ACTIVITY_LOG, data);
};

export const callTrackBlogUpvote = async (data: {
  blogId: string;
  userId: string;
  action: 'upvote' | 'downvote';
}) => {
  return callFunction(FUNCTION_ENDPOINTS.TRACK_BLOG_UPVOTE, data);
};

export const callInventoryTracker = async (data: {
  storeId: string;
  action: string;
  itemId: string;
  quantity: number;
  userId: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.INVENTORY_TRACKER, data);
};

export const callUpdateMenuAvailability = async (data: {
  storeId: string;
  menuItemId: string;
  isAvailable: boolean;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.UPDATE_MENU_AVAILABILITY, data);
};

export const callCheckSubscription = async (data: {
  userId: string;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.CHECK_SUBSCRIPTION, data);
};

export const callGetVapidPublicKey = async () => {
  return callFunction(FUNCTION_ENDPOINTS.GET_VAPID_PUBLIC_KEY);
};

export const callSendWebPushNotification = async (data: {
  subscription: PushSubscription;
  title: string;
  body: string;
  data?: Record<string, unknown>;
}) => {
  return callFunction(FUNCTION_ENDPOINTS.SEND_WEB_PUSH_NOTIFICATION, data);
};

// Export the api instance for custom calls
export { api }; 